<template>
  <div v-if="!loading">
    <ProductSelectorTemplated
      v-if="isTemplatedProduct"
      :product-type="productType"
      :products="productOptions"
      :legs="legOptions"
      :cushions="cushionOptions"
      :fabrics="fabricOptions"
      :active-product="activeProduct"
      :default-product="defaultProduct"
      :is-printout="printout"
      :hide-call="hideCall"
      :shipping="shippingObject"
      :other-options="otherOptions"
      @activate-product="maybeActivateProduct"
      @set-attribute-state="maybeAddAttributeState"
      @remove-attribute-state="maybeRemoveAttributeState"
    />
    <ProductSelectorVariable
      v-else
      :active-product="activeProduct"
      :default-variant="activeVariant"
      :shipping="shippingObject"
      @variant-selected="handleVariantSelected"
    />
  </div>
</template>
<script>
import ProductSelectorTemplated from "./ProductSelectorTemplated";
import ProductSelectorVariable from "./ProductSelectorVariable.vue";
import smoothscroll from "smoothscroll-polyfill";
export default {
  name: "ProductAddToCartController",
  components: {
    ProductSelectorTemplated,
    ProductSelectorVariable,
  },
  props: {
    productType: {
      type: String,
      default: "Plain",
    },
    products: {
      type: Array,
    },
    legs: {
      type: Array,
    },
    fabrics: {
      type: Array,
    },
    cushions: {
      type: Array,
    },
    shippingTimes: {
      type: Array,
    },
    isPrintout: Boolean,
    ctaHidden: Boolean,
    otherOptionsObject: Object
  },
  data() {
    return {
      productOptions: [],
      shippingObject: this.shippingTimes || window.spsShippingTimes,
      legOptions: this.legs || window.spsLegs,
      fabricOptions: this.fabrics || window.spsFabrics,
      cushionOptions: this.cushions || window.spsCushions,
      printout: this.isPrintout || window.isPrintout,
      hideCall: this.ctaHidden || window.hideCTA,
      otherOptions: this.otherOptionsObject || window.spsOtherOptions,
      activeProduct: {},
      defaultProduct: {},
      activeVariant: {},
      loading: true,
      regularProduct: false,
    };
  },
  mounted() {
    smoothscroll.polyfill();
    this.productOptions = this.preprocessOptions();
    this.activeProduct = this.deriveActiveProduct();
    if (!this.isTemplatedProduct) this.determineBaseVariant();
    if (this.isTemplatedProduct) this.setHistoryState();
    this.loading = false;
  },
  updated() {
    this.maybeUpdateAffirm();
  },
  computed: {
    searchParameterKeys() {
      // NOTE this is pulling all the keys out of the url for later cleanup, need to watch out for product specific logic
      // that adds or deletes keys, need to add them into it here based on the product type
      let urlKeys = [
        ...new URLSearchParams(this.productOptions[0].query).keys(),
      ];
      if (this.productType === "U-Shaped Sectional With Chaise" || this.productType === "Sectional With Chaise") {
        // This is dynamic based off the orienation selected
        urlKeys.push("width_1", "width_3");
      }
      return urlKeys;
    },
    isTemplatedProduct() {
      return ![
        "Rug",
        "Table",
        "Table Lamp",
        "Pendant",
        "Dropshipping",
        "Dresser",
        "Nightstand"
      ].includes(this.productType);
    },
  },
  methods: {
    handleVariantSelected(variant) {
      const queryObject = {
        attribute: "variant",
        value: variant.variantId,
      };
      this.activeVariant = variant;
      this.maybeAddAttributeState(queryObject);
      this.maybeUpdateAffirm();
    },
    determineBaseVariant() {
      const queryVars = new URLSearchParams(window.location.search);
      if (queryVars.has("variant")) {
        const productVariantIds = this.activeProduct.variants.map(
          (variant) => variant.variantId
        );
        const variantId = queryVars.get("variant");
        for (let x = 0; x < productVariantIds.length; x++) {
          if (variantId === productVariantIds[x]) {
            this.activeVariant = this.activeProduct.variants[x];
            return;
          }
        }
      }
      this.activeVariant = this.activeProduct.variants[0];
    },
    preprocessOptions() {
      if (this.isTemplatedProduct) {
        const optionKeys = ["fabric", "leg"];
        const mappingKeys = ["cushion"]
        const keyMappings = this.getOptionValueMapping()
        const options = this.products || window.spsProducts;
        return options.map((option) => {
          const optionCopy = Object.assign({}, option);
          const additionalComponents = [];
          optionKeys.forEach((key) => {
            if (option[key]) {
              additionalComponents.push(
                `${key}=${encodeURIComponent(option[key]).replaceAll(
                  "%20",
                  "+"
                )}`
              );
            }
          });
          mappingKeys.forEach((key) => {
            if (keyMappings[option[key]]) {
              additionalComponents.push(
                `${key}=${encodeURIComponent(keyMappings[option[key]]).replaceAll(
                  "%20",
                  "+"
                )}`
              );
            }
          })
          if (additionalComponents.length > 0) {
            const baseQuery = optionCopy["query"];
            if (baseQuery !== "?") {
              optionCopy["query"] = [
                baseQuery,
                additionalComponents.join("&"),
              ].join("&").replaceAll('(','%28').replaceAll(')','%29');
            } else {
              optionCopy["query"] = baseQuery + additionalComponents.join("&");
            }
          }
          return optionCopy;
        });
      }
      return this.products || window.spsProducts;
    },
    getOptionValueMapping() {
      // TODO we need to move all these over to SKU CODE
      return {
        // Cushions
        "Foam & Trillium Package":"CT",
        "Foam & Fiber Package":"CF",
        "Standard":"CF",
        "Down Alternative":"CT",
        "Like Butter Foam & Trillium":"CD",
        "Foam Bench Seat & Fiber Pillow Package":"BFP",
        "Foam Seat & Fiber Pillow Package":"CFP",
      }
    },
    getQueryParts(searchParameters) {
      const additionalParameters = new URLSearchParams();

      for (const key of searchParameters.keys()) {
        if (!this.searchParameterKeys.includes(key)) {
          additionalParameters.set(key, searchParameters.get(key));
          searchParameters.delete(key);
        }
      }
      // Hack to get this out if it bypasses
      if (searchParameters.has("color")) {
        additionalParameters.set("color", searchParameters.get("color"));
        searchParameters.delete("color");
      }

      return [searchParameters, additionalParameters];
    },
    maybeActivateProduct(searchParameters) {
      // eslint-disable-next-line
      const [cleanedParams, additionalParams] =
        this.getQueryParts(searchParameters);
      const mappingKeys = ["cushion"]
      const mappedParams = this.getOptionValueMapping()
      const matchingProduct = this.productOptions.find((product) => {
        let match = true;
        
        for (const [key, value] of cleanedParams) {
          const mappedValue = mappedParams[product[key]]
          if (mappedValue && mappingKeys.includes(key)) {
            if (mappedValue !== value.trim()) {
              match = false
            }
          } else if (!product[key] || product[key] !== value.trim()) {
            match = false;
          }
        }
        return match;
      });

      if (matchingProduct) {
        // Forcing children to update as there is a new object set
        this.activeProduct = Object.assign({}, matchingProduct);
      } else {
        console.log(this.productOptions)
        console.log("NO MATCHING PRODUCT");
        console.log("?" + cleanedParams.toString());
        this.activeProduct = Object.assign({}, this.activeProduct);
      }
      this.maybeUpdateAffirm();
      this.setHistoryState();
      this.triggerUIUpdates(this.activeProduct)
    },
    maybeUpdateAffirm() {
      const activePrice = this.activeProduct.price
        ? this.activeProduct.price
        : this.activeVariant.price;
      const affirmIdentifiers = [
        ".td--product-affirm",
        ".affirm-product-modal",
        "#affirmProductModal",
        "#affirmLearnMore",
      ];
      affirmIdentifiers.forEach((identifier) => {
        const element = document.querySelector(identifier);
        if (element) {
          const priceAsShopifyCurrency =
            parseFloat(activePrice.replaceAll(/[$]|[,]/g, "")) * 100;
          element.setAttribute("data-amount", priceAsShopifyCurrency);
        }
      });
      if (window.affirm) {
        try {
          window.affirm.ui.ready(function () {
            window.affirm.ui.refresh();
            setTimeout(() => document.dispatchEvent(new Event('update-custom-affirm')), 100)
          });
        } catch (err) {
          console.log(err);
          console.log("affirm ui refresh error");
        }
      }
    },
    deriveActiveProduct() {
      // eslint-disable-next-line
      let defaultProduct = this.productOptions.find(
        (option) => option.sku === window.defaultSku
      );
      if (defaultProduct) {
        this.triggerUIUpdates(defaultProduct)
        this.defaultProduct = defaultProduct;
      }

      const [cleanedParams, additionalParams] = this.getQueryParts(
        new URLSearchParams(window.location.search)
      );
      if (additionalParams.has("variant")) {
        const variantId = additionalParams.get("variant");
        const defaultProduct = this.productOptions.find(
          (option) => option.id == variantId
        );
        if (defaultProduct) {
          this.triggerUIUpdates(defaultProduct)
          return defaultProduct;
        }
      }
      const searchParams = "?" + cleanedParams.toString();
      if (searchParams) {
        const defaultProduct = this.productOptions.find(
          (option) => option.query == searchParams
        );
        if (defaultProduct) {
          this.triggerUIUpdates(defaultProduct)
          return defaultProduct;
        }
      }
      if (defaultProduct) {
        this.triggerUIUpdates(defaultProduct)
        return defaultProduct;
        
      }
      this.defaultProduct = this.productOptions[0];
      this.triggerUIUpdates(defaultProduct)
      return this.productOptions[0];
    },
    setHistoryState() {
      // eslint-disable-next-line
      const [cleanedParams, additionalParams] = this.getQueryParts(
        new URLSearchParams(window.location.search)
      );

      let searchParams = this.activeProduct.query;
      if (additionalParams.toString()) {
        // If it has the variant remove the variant so that we can have it function normally
        if (additionalParams.has("variant")) additionalParams.delete("variant");
        searchParams += "&" + additionalParams.toString();
      }
      
      const newPath = window.location.pathname + searchParams;
      history.replaceState(null, "", newPath);
    },
    maybeAddAttributeState(event) {
      const searchParams = new URLSearchParams(window.location.search);
      searchParams.set(event.attribute, event.value);
      const newPath = window.location.pathname + "?" + searchParams.toString();
      history.replaceState(null, "", newPath);
    },
    maybeRemoveAttributeState(event) {
      const searchParams = new URLSearchParams(window.location.search);
      if (searchParams.has(event.attribute)) {
        searchParams.delete(event.attribute);
      }
      const newPath = window.location.pathname + "?" + searchParams.toString();
      history.replaceState(null, "", newPath);
    },
    triggerUIUpdates(product) {
      if (product) {
        document.dispatchEvent(new CustomEvent('bmm-product-update', {
          detail: { price: product.price, normalPrice: product.sPrice },
        }))
      }
    }
  },
};
</script>
<style scoped>
.atc-wrapper {
  /* display: flex; */
  /* flex-direction: column; */
}
.btn--add-to-cart {
  min-width: 155px;
  padding: 0;
  min-width: 240px;
  min-height: 56px;
  border-radius: 6px;
  background-color:  #cfb52c;
  background-image: none;
  color: #333;
  text-transform: uppercase;
  font-family: var(--oddit-main-font);;
  font-size: 14px;
  line-height: 17px;
  font-weight: 500;
  letter-spacing: 0.25px;
  flex: auto;
  border: 0;
}
.product-single__price {
  color: #393534;
  font-family: var(--oddit-heading-font);
  font-size: 2.125rem;
  font-weight: 600;
  letter-spacing: 0.25px;
  line-height: 41px;
  opacity: 0.87;
}
.product-discount-percent {
  margin-left: 15px;
}
</style>
